import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'

// React 17 has no useId; a module-level counter gives each mounted modal a
// unique title id so concurrent modals don't collide on aria-labelledby.
let modalTitleSeq = 0
const nextModalTitleId = () => {
  modalTitleSeq += 1
  return `public-modal-title-${modalTitleSeq}`
}

export default function Modal({
  children,
  title,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  disabled = false,
  closeOnEscape = false,
}) {
  const modalRef = useRef(null)
  const [titleId] = useState(nextModalTitleId)

  // Opt-in Escape-to-dismiss (default off). Esc is a standard dialog
  // expectation, but it's opt-in because closing semantics differ per
  // consumer: some callers' onCancel navigates away, so we don't want a
  // stray Escape to trigger that unless the caller asked for it. Separate
  // effect (deps include closeOnEscape/onCancel) so the focus/trap effect
  // below keeps its empty deps and doesn't re-run / re-focus on re-render;
  // the early return means consumers that don't opt in add no listener.
  useEffect(() => {
    if (!closeOnEscape || typeof onCancel !== 'function') { return undefined }
    const onKey = (e) => { if (e.key === 'Escape') { onCancel() } }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [closeOnEscape, onCancel])

  // Dialog semantics (role/aria-modal/aria-labelledby below) plus focus
  // management so keyboard/screen-reader users aren't able to tab into the
  // background page while the modal is open: move focus into the dialog on
  // open, and trap Tab/Shift+Tab between the first and last focusable
  // elements. (No Esc-to-cancel here on purpose — closing semantics differ
  // per consumer; keep the shared component's behavior limited to the
  // universally-safe a11y so it doesn't change any caller's onCancel flow.)
  useEffect(() => {
    const node = modalRef.current
    if (!node) { return undefined }
    // Remember what had focus before the modal opened so we can return focus
    // there on close — otherwise keyboard users are left at the top of the tab
    // order after dismissing the dialog.
    const previouslyFocused = document.activeElement
    node.focus()

    // Listen on document, not node: when every interactive element is
    // disabled (e.g. both buttons while a submit is in flight) the browser
    // drops focus to <body>, so a node-scoped listener would never see the
    // Tab keydown and focus could walk into the background page. A
    // document-scoped listener catches Tab wherever focus currently sits.
    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') { return }
      const focusable = node.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      if (focusable.length === 0) {
        // Nothing inside is focusable (e.g. both buttons disabled mid-submit):
        // keep focus on the dialog container itself (tabIndex=-1) rather than
        // letting Tab escape to the background page.
        e.preventDefault()
        node.focus()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement
      if (!node.contains(active)) {
        // Focus has escaped (or never entered) the dialog — pull it back in.
        e.preventDefault()
        first.focus()
      } else if (e.shiftKey && active === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (typeof previouslyFocused?.focus === 'function') {
        previouslyFocused.focus()
      }
    }
  }, [])

  return ReactDOM.createPortal(
    <>
      <div className="public-modal-shadow" />
      <div
        ref={modalRef}
        className="public-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className="public-modal-header">
          <span className="fs-4" id={titleId}>{title}</span>
        </div>
        <div className="public-modal-body">
          { children }
        </div>
        <div className="public-modal-footer">
          {onCancel && (
            <button type="button" className="btn btn-secondary me-3" onClick={onCancel} disabled={disabled}>{cancelText}</button>
          )}
          <button type="button" className="btn btn-primary" onClick={onConfirm} disabled={disabled}>
            {confirmText}
          </button>
        </div>
      </div>
    </>,
    document.body,
  )
}

Modal.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  disabled: PropTypes.bool,
  closeOnEscape: PropTypes.bool,
}
